/**
 * useAgentInbox.types — agent_signals row shape + send payload + known
 * agent constants. Split from useAgentInbox.ts to keep the parent under
 * the 300 LOC cap.
 */

export interface AgentSignal {
  id: string
  agent_name: string | null
  signal_type: string
  /**
   * ADR-040 stream/reply split. Older rows may have null/absent kind.
   * `reply` = addressed message, `stream` = narration/archive.
   */
  kind?: string | null
  /**
   * Comms-derived chat lane (`v_chat_inbox` view, Gate 2 of
   * MO-FLEET-IDENTITY-CONVERGENCE-V1). Server-computed from
   * `(kind, signal_type, target IS NULL)` against the signal_type
   * allowlist. One of `konversation` / `narration` / `eskalation` /
   * `entscheidung`. Null on rows from the legacy `agent-signals` slug
   * (pre-Gate-4) or rows the view excludes from chat surface.
   */
  lane?: string | null
  source: string | null
  target: string | null
  payload: { text?: string; in_reply_to?: string; [key: string]: unknown }
  consumed: boolean
  consumed_at: string | null
  created_at: string
  context: Record<string, unknown> | null
  /**
   * AMD-5 v2 envelope (PROPOSAL-ADR-039-AMD-5 §4 + ADR-039-AMD-2 §2).
   * Pre-AMD-5 rows have NULL — fleet-fanout, no embodiment scope.
   */
  from_embodiment_id?: string | null
  target_embodiment_id?: string | null
  session_id?: string | null
  from_call_sign?: string | null
  from_work_item_id?: string | null
}

export interface SendMessageAttachment {
  url: string
  public_url?: string
  mime_type: string
  filename: string
  size?: number
}

export interface SendMessagePayload {
  target: string
  text: string
  /** Optimistic-cache key override for group sends. */
  useGroupInboxCache?: boolean
  inboxRoom?: string
  /**
   * Optimistic-cache routing keys mirrored from the bound chat read
   * (`useAgentInboxPaginated(agent, sessionFilter, lane)`). The chat
   * surface reads an InfiniteData query keyed
   * `[...inboxKeys.feed(agent, sessionFilter, lane), 'paginated']`; the
   * optimistic write MUST land on that exact key + shape or the operator
   * never sees their own message (BUG-FE-BRIDGE-CHAT-OPTIMISTIC-KEY-MISMATCH).
   * Stripped before the wire round-trip — never reach the SL body.
   */
  inboxSessionFilter?: string | null
  inboxLane?: string | null
  /**
   * Optional persona-embodiment routing key. When set, SL writes
   * `agent_signals.target_embodiment_id = <uuid>` so the signal lands
   * on a specific embodiment instead of being broadcast across the
   * persona's fan-in queue.
   */
  target_embodiment_id?: string
  /**
   * Optional workitem-thread routing key (operator 2026-05-23,
   * FE-CMD-BRIDGE-WORKITEM-CHAT-ATTACH). When set, SL writes
   * `agent_signals.from_work_item_id = <uuid>` so the discussion lands
   * on the workitem-thread surface instead of the flat chat lane. The
   * column already exists on `agent_signals` and is projected through
   * `v_chat_inbox` / `ChatInboxItemSchema` (no schema or IACP change).
   */
  from_work_item_id?: string
  /**
   * §A5 image attachments. FE uploads files via SL `/v1/commander/uploads`
   * first, then includes the canonical URLs here; SL writes them to
   * `agent_signals.payload.attachments[]`. Per Commander 2026-05-06 22:56Z.
   */
  attachments?: SendMessageAttachment[]
}

export const KNOWN_AGENTS = [
  'AWACS',
  'Tower ATC',
  'DEMON HUNTER',
  'God Mode',
  'Raider Intel',
  'Frontend',
  'Service Layer',
  'Comms',
  'LLM',
] as const

export type AgentName = (typeof KNOWN_AGENTS)[number]

export type BadgeVariant =
  | 'blue' | 'teal' | 'orange' | 'violet'
  | 'magenta' | 'cyan' | 'green' | 'default' | 'secondary'

export const AGENT_BADGE: Record<string, BadgeVariant> = {
  'AWACS': 'blue',
  'Tower ATC': 'teal',
  'DEMON HUNTER': 'orange',
  'God Mode': 'violet',
  'Raider Intel': 'magenta',
  'Frontend': 'cyan',
  'Service Layer': 'green',
  'Comms': 'secondary',
  'LLM': 'secondary',
  'commander': 'default',
}

export const inboxKeys = {
  all: ['agent-inbox'] as const,
  feed: (agent?: string, sessionFilter?: string | null, lane?: string | null) =>
    [
      'agent-inbox',
      'feed',
      agent !== undefined ? agent : 'all',
      sessionFilter !== null && sessionFilter !== undefined ? sessionFilter : 'all-sessions',
      lane !== null && lane !== undefined ? lane : 'all-lanes',
    ] as const,
}
